export interface DocumentPictureInPicture {
  requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>
  window: Window | null
}

export function readDocumentPictureInPicture(
  target: object | null | undefined,
): DocumentPictureInPicture | null {
  if (!target || !('documentPictureInPicture' in target)) {
    return null
  }
  const pip = target.documentPictureInPicture
  if (!isPictureInPicture(pip)) {
    return null
  }
  return pip
}

export function supportsDocumentPictureInPicture(target: object | null | undefined): boolean {
  return readDocumentPictureInPicture(target) !== null
}

function isPictureInPicture(value: unknown): value is DocumentPictureInPicture {
  if (!value || typeof value !== 'object' || !('requestWindow' in value)) {
    return false
  }
  return typeof value.requestWindow === 'function'
}

// Percentage height on the shared message list resolves to auto in this
// document, so the list grows and the window scrolls. Lock the window and
// make that list a flex child with its own overflow, and suppress the
// overscroll bounce so it cannot chain out of the list.
export const PIP_SHELL_STYLE = [
  'html,body,#aida-pip-root{height:100%;margin:0;overflow:hidden;overscroll-behavior:none}',
  '#aida-pip-root{min-height:0}',
  '#aida-pip-root .scroller-hover-parent:has(> .overflow-y-auto){display:flex;flex-direction:column;min-height:0;overflow:hidden;overscroll-behavior:none}',
  '#aida-pip-root .scroller-hover-parent > .overflow-y-auto{flex:1 1 auto;min-height:0;height:auto;overflow-y:auto;overscroll-behavior:none;touch-action:pan-y}',
  '#aida-pip-root [data-slate-editor="true"]{overscroll-behavior:none;touch-action:pan-y}',
  'html,body,#aida-pip-root,#aida-pip-root *{-webkit-app-region:no-drag !important;app-region:no-drag !important}',
].join('')

export function mountPictureInPictureShell(pipDocument: Document) {
  const root = pipDocument.createElement('div')
  root.id = 'aida-pip-root'
  for (const node of [pipDocument.documentElement, pipDocument.body, root]) {
    node.style.height = '100%'
    node.style.margin = '0'
    node.style.overflow = 'hidden'
    node.style.overscrollBehavior = 'none'
  }
  root.style.minHeight = '0'
  for (const node of [pipDocument.documentElement, pipDocument.body, root]) {
    node.style.setProperty('-webkit-app-region', 'no-drag', 'important')
    node.style.setProperty('app-region', 'no-drag', 'important')
  }
  const layout = pipDocument.createElement('style')
  layout.textContent = PIP_SHELL_STYLE
  pipDocument.head.append(layout)
  pipDocument.body.replaceChildren(root)
  return root
}

export function copyDocumentStyles(source: Document, target: Document) {
  target.documentElement.className = source.documentElement.className
  const styleAttr = source.documentElement.getAttribute('style')
  if (styleAttr) {
    target.documentElement.setAttribute('style', styleAttr)
  }
  for (const node of source.querySelectorAll('link[rel="stylesheet"], style')) {
    target.head.appendChild(node.cloneNode(true))
  }
  const adopted = source.adoptedStyleSheets
  if (adopted.length > 0) {
    try {
      target.adoptedStyleSheets = [...adopted]
    } catch {
      // A browser that refuses shared sheets still has the cloned style tags.
    }
  }
}
